import styled, { css } from 'styled-components'
import {
  StyledHeaderProps,
  NavSectionProps,
  NavItemProps,
} from './Header.types'

export const StyledHeader = styled.header<StyledHeaderProps>`
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  padding: ${({ theme }) => `${theme.spacing.medium} ${theme.spacing.large}`};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.large};
  height: ${({ theme }) => theme.layout.appHeaderHeight};
  position: absolute;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.header};

  @media print {
    display: none !important;
    visibility: hidden !important;
  }

  ${(props) =>
    props.$sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: ${props.theme.zIndex.sticky};
      box-shadow: none;
    `}

  ${(props) =>
    props.$variant === 'centered' &&
    css`
      justify-content: center;
    `}
  ${(props) =>
    props.$variant === 'minimal' &&
    css`
      justify-content: space-between;
      padding: ${({ theme }) =>
        `${theme.spacing.small} ${theme.spacing.medium}`};
      border-bottom: none;
    `}
  ${(props) =>
    props.$variant === 'default' &&
    css`
      justify-content: space-between;
    `}
`

export const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-shrink: 0;
`

export const NavSection = styled.nav<NavSectionProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xlarge};

  ${(props) =>
    props.$variant === 'default' &&
    css`
      flex: 1;
      margin-left: ${props.theme.spacing.large};
    `}
`

export const NavItemWrapper = styled.div`
  position: relative;
`

export const NavItem = styled.button<NavItemProps>`
  background: none;
  border: none;
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${(props) =>
    props.$active
      ? props.theme.colors.primary
      : props.theme.colors.text.secondary};
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
  position: relative;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const SubmenuArrow = styled.svg`
  width: 12px;
  height: 12px;
  transition: transform 0.2s;
`

export const SubmenuContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  min-width: 200px;
  z-index: 1000;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '-10px')});
  transition: all 0.2s ease;
  margin-top: 4px;
`

export const SubmenuItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 20px;
  border: none;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.background.tertiary : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 18px;
    height: 18px;
    margin-right: 12px;
  }
`

export const SearchSection = styled.div`
  flex: 1;
  max-width: 400px;
`

export const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-shrink: 0;
`

export const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.small};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`

export const DesktopOnly = styled.div`
  display: flex;
  align-items: center;
  gap: inherit;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`

export const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
`

export const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .icon {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }
`

export const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  cursor: pointer;
  padding: 4px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: background 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const UserName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
`

export const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(100vh - 64px);
  background: ${({ theme }) => theme.colors.background.primary};
  z-index: ${({ theme }) => theme.zIndex.modal - 1};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.medium};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`

export const MobileNavSection = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

export const MobileNavItem = styled.button<NavItemProps>`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${(props) =>
    props.$active
      ? props.theme.colors.primary
      : props.theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: left;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.primary};
  }

  ${(props) =>
    props.$active &&
    css`
      background: ${props.theme.colors.background.tertiary};
    `}
`

export const MobileSubmenu = styled.div<{ $isOpen: boolean }>`
  max-height: ${({ $isOpen }) => ($isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`

export const MobileSubmenuItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) =>
    `${theme.spacing.small} ${theme.spacing.medium} ${theme.spacing.small} ${theme.spacing.xlarge}`};
  border: none;
  background: ${({ $active }) =>
    $active ? 'rgba(0, 0, 0, 0.02)' : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};

  &:hover {
    background: rgba(0, 0, 0, 0.02);
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
    margin-right: 10px;
  }
`
